import { createContext, useContext, useMemo, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSubscription } from "@/hooks/useSubscription";

interface FreemiumContextType {
  isPreview: boolean;
  loading: boolean;
  requireSubscription: () => boolean;
  openUpgradeDialog: () => void;
}

const FreemiumContext = createContext<FreemiumContextType | undefined>(undefined);

export function FreemiumProvider({ children }: { children: ReactNode }) {
  const { isActive, loading } = useSubscription();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isPreview = !loading && !isActive;

  const openUpgradeDialog = () => setOpen(true);

  const requireSubscription = () => {
    if (loading) return false;
    if (!isPreview) return true;
    setOpen(true);
    return false;
  };

  const value = useMemo(
    () => ({ isPreview, loading, requireSubscription, openUpgradeDialog }),
    [isPreview, loading],
  );

  return (
    <FreemiumContext.Provider value={value}>
      {children}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle> This feature requires a subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Upgrade your plan to unlock this and all other features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Maybe Later</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setOpen(false);
                navigate("/billing");
              }}
            >
              View Plans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FreemiumContext.Provider>
  );
}

export function useFreemium() {
  const context = useContext(FreemiumContext);
  if (!context) throw new Error("useFreemium must be used within FreemiumProvider");
  return context;
}
